import { useChatsVirtualizer } from '../hooks/useChatsVirtualizer'
import { Link } from 'react-router-dom'
import { Retry } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { SortableHeaderButton } from '@/components/sortable-header/SortableHeaderButton'
import AgentV2ConversationMenu from './AgentV2ConversationMenu'

export type ChatsListRow = {
  id: string
  title: string
  owner: string
  href: string
  activityLabel: string
  updatedAt: string
}

type ChatsListProps = {
  rows: ChatsListRow[]
  scrollElement: HTMLDivElement | null
  isLoading: boolean
  isError: boolean
  searchQuery: string
  sortDirection: 'asc' | 'desc'
  onSort: () => void
  onRetry: () => void
  onClearSearch: () => void
  onOpen: () => void
  onRename: (id: string) => void
  onDelete: (id: string) => void
}

export function ChatsList({
  scrollElement,
  rows,
  isLoading,
  isError,
  searchQuery,
  sortDirection,
  onSort,
  onRetry,
  onClearSearch,
  onOpen,
  onRename,
  onDelete,
}: ChatsListProps) {
  return (
    <div className="min-w-0 [container-type:inline-size]">
      {isLoading ? (
        <div role="status" aria-label="Loading chats">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="flex h-[52px] items-center justify-between gap-8 border-b border-brand-neutral-200">
              <LoadingSkeleton width="60%" lineHeight="20px" />
              <LoadingSkeleton width="15%" lineHeight="16px" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="py-4">
          <div className="flex min-h-[144px] w-full items-center justify-center rounded-md border border-brand-neutral-200 px-6 py-4">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-sm leading-5 text-brand-neutral-800">Unable to load chats for this matter.</p>
              <Button variant="tonal" color="secondary" size="sm" leadingIcon={<Retry className="size-4" />} onClick={onRetry}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      ) : rows.length === 0 ? (
        searchQuery.trim() ? (
          <div role="status" className="flex h-[68px] items-center justify-center bg-white">
            <div className="flex max-w-[90%] items-center gap-3 px-2 py-2">
              <p className="flex min-w-0 items-center text-xs [@container_(min-width:_640px)]:text-sm">
                <span className="shrink-0">
                  No <span className="hidden [@container_(min-width:_640px)]:inline">Search </span>Results for &ldquo;
                </span>
                <span className="min-w-0 truncate">{searchQuery.trim()}</span>
                <span className="shrink-0">&rdquo;</span>
              </p>
              <Button onClick={onClearSearch} size="xs" variant="tonal" color="neutral">
                Clear
              </Button>
            </div>
          </div>
        ) : (
          <div role="status" className="py-8 text-center text-sm text-brand-neutral-600">
            <p>No chats in this matter yet.</p>
          </div>
        )
      ) : (
        <>
          <div className="flex h-11 items-center gap-8 border-b border-brand-neutral-200 text-sm font-medium text-brand-neutral-800">
            <span className="min-w-0 flex-1 pl-1">Name</span>
            <span className="hidden w-[95px] shrink-0 sm:block">Owner</span>
            <div className="-mr-1 shrink-0">
              <SortableHeaderButton label="Last Activity" isSorted={sortDirection} onSort={onSort} />
            </div>
          </div>
          <VirtualChatRows rows={rows} scrollElement={scrollElement} onOpen={onOpen} onRename={onRename} onDelete={onDelete} />
        </>
      )}
    </div>
  )
}

function VirtualChatRows({
  rows,
  scrollElement,
  onOpen,
  onRename,
  onDelete,
}: Pick<ChatsListProps, 'rows' | 'scrollElement' | 'onOpen' | 'onRename' | 'onDelete'>) {
  const { listRef, setFirstRow, virtualizer, scrollMargin, setFocusedId, setMenuId } = useChatsVirtualizer(rows, scrollElement)
  return (
    <ul ref={listRef} aria-label="Chats" className="relative" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index]
        return (
          <li
            key={row.id}
            ref={virtualRow.index === 0 ? setFirstRow : undefined}
            aria-posinset={virtualRow.index + 1}
            aria-setsize={rows.length}
            style={{ transform: `translateY(${virtualRow.start - scrollMargin}px)` }}
            onFocusCapture={() => setFocusedId(row.id)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setFocusedId(null)
            }}
            className="group absolute left-0 top-0 w-full flex min-h-[52px] items-center gap-3 border-b border-brand-neutral-200 px-1 py-4 hover:bg-brand-neutral-100 focus-within:bg-brand-neutral-100 sm:h-[52px] sm:gap-8 sm:py-0"
          >
            <div className="min-w-0 flex-1">
              <Link
                to={row.href}
                onClick={onOpen}
                title={row.title}
                className="block truncate font-rethink-sans text-sm font-semibold leading-5 text-brand-neutral-950 after:absolute after:inset-0 focus:outline-hidden focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-brand-500"
              >
                {row.title}
              </Link>
              <span className="mt-1 block truncate text-xs text-brand-neutral-600 sm:hidden">{row.owner}</span>
            </div>
            <span title={row.owner} className="hidden w-[95px] shrink-0 truncate text-xs font-medium text-brand-neutral-800 sm:block">
              {row.owner}
            </span>
            <div className="pointer-events-none relative flex w-[111px] shrink-0 items-center justify-end gap-1 text-right text-xs font-medium text-brand-neutral-800 lg:block">
              <time
                dateTime={row.updatedAt}
                title={new Date(row.updatedAt).toLocaleString()}
                className="lg:block lg:group-hover:invisible lg:group-focus-within:invisible lg:group-has-[[data-state=open]]:invisible"
              >
                {row.activityLabel}
              </time>
              <div className="pointer-events-auto flex justify-end lg:absolute lg:inset-y-0 lg:right-0 lg:items-center">
                <AgentV2ConversationMenu
                  conversationId={row.id}
                  onOpenChange={(open) => setMenuId(open ? row.id : null)}
                  onSelectRename={() => onRename(row.id)}
                  onSelectDelete={() => onDelete(row.id)}
                  showTriggerOnHover
                  triggerAriaLabel={`Chat actions: ${row.title}`}
                />
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
